import { PropTypes } from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  useEffect,
} from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Controller, FormProvider, useForm, useWatch, useFieldArray } from 'react-hook-form';
import {
  fieldRequired,
  Input,
  requiredDatePicker,
  requiredInput,
  requiredSelect,
} from '../../../core/formValidation';
import { toString as enumToString } from '../../../core/utils';
import { isNumber } from '../../../utils/number-util';
import { UtilityType } from '../../expenses/models';
import { selectActiveUtilities } from '../../settings/reducer';

/* const lineItemOptionsByType = {
   Water: [
     { value: 'base', text: 'Base' },
     { value: 'waterUsage', text: 'Water Usage' },
     { value: 'tieredUsageCharge', text: 'Tiered Usage Charge' },
     { value: 'Meter Charge', text: 'Meter Charge' },
     { value: 'taxes', text: 'Taxes & Fees' },
     { value: 'other', text: 'Other' },
   ],
   Electric: [
     { value: 'base', text: 'Base' },
     { value: 'electricityUsage', text: 'Electricity Usage(kWh)' },
     { value: 'generationCharge', text: 'Generation Charge' },
     { value: 'transmissionCharge', text: 'Transmission Charge' },
     { value: 'distributionCharge', text: 'Distribution Charge' },
     { value: 'fuel', text: 'Fuel/Power Cost Adjustment' },
     { value: 'renewableFee', text: 'Renewable/Green Energy Fee' },
     { value: 'taxes', text: 'Taxes & Fees' },
     { value: 'other', text: 'Other' },
   ],
   Gas: [
     { value: 'base', text: 'Base' },
     { value: 'gasUsage', text: 'Gas Usage(Therms)' },
     { value: 'gasSupplyCommodityCharge', text: 'Gas Supply/Commodity Charge' },
     { value: 'deliveryDistributionCharge', text: 'Delivery/Distribution Charge' },
     { value: 'weatherAdjustment', text: 'Weather Adjustment' },
     { value: 'taxes', text: 'Taxes & Fees' },
     { value: 'other', text: 'Other' },
   ],
   Sewer: [
     { value: 'base', text: 'Base' },
     { value: 'serviceCharge', text: 'Sewer Service Charge' },
     { value: 'sewerUsageCharge', text: 'Sewer Usage Charge' },
     { value: 'wasteWaterTreatmentFee', text: 'Waste Water Treatment Fee' },
     { value: 'environmentalFee', text: 'Environmental Compliance Fee' },
     { value: 'taxes', text: 'Taxes & Fees' },
     { value: 'other', text: 'Other' },
   ],
 }; */

const UtilityEditForm = forwardRef(
  (
    {
      utilityUnits,
      activeUtilities,
      currentUtility,
      intl,
      onSubmit,
      initialValues,
      multiBill: multiBillProp,
      onMultiBillChange,
      setSubmitDisabled,
    },
    ref
  ) => {
    const sourceLineItems = Array.isArray(initialValues?.lineItems)
      ? initialValues.lineItems
      : Array.isArray(initialValues?.items)
      ? initialValues.items
      : Array.isArray(initialValues?.line_items)
      ? initialValues.line_items
      : [];

    const mappedLineItems = sourceLineItems.map((li) => ({
      name: li?.name ?? '',
      description: li?.description ?? '',
      bop: li?.startReading ?? '',
      eop: li?.endReading ?? '',
      amount: li?.cost ?? '',
      type:
        typeof li?.type === 'number'
          ? Number(li.type)
          : typeof li?.type === 'string' && UtilityType[li.type] !== undefined
          ? UtilityType[li.type]
          : undefined,
    }));

    const methods = useForm({
      defaultValues: {
        type: initialValues?.type ?? UtilityType[currentUtility],
        multiBills: initialValues?.multiBills ?? [],
        ...(initialValues || {}),
        lineItems: mappedLineItems,
      },
    });
    const { control, setValue, handleSubmit, reset } = methods;
    const [internalMultiBill, setInternalMultiBill] = useState(
      (initialValues?.lineItems || []).length > 0
    );
    const isControlledMultiBill = typeof multiBillProp === 'boolean';
    const isMultiBill = isControlledMultiBill ? multiBillProp : internalMultiBill;
    const watchedType = useWatch({ control, name: 'type' });
    const {
      fields: lineItemFields,
      append: appendLineItem,
      remove: removeLineItem,
      replace: replaceLineItems,
    } = useFieldArray({ control, name: 'lineItems' });
    const watchedLineItems = useWatch({ control, name: 'lineItems' }) || [];
    const watchedCost = useWatch({ control, name: 'cost' });
    const watchedStart = useWatch({ control, name: 'start' });
    const watchedEnd = useWatch({ control, name: 'end' });
    const watchedStartReading = useWatch({ control, name: 'startReading' });
    const watchedEndReading = useWatch({ control, name: 'endReading' });
    const watchedUnit = useWatch({ control, name: 'unit' });
    const getDatePickerDate = (value) => {
      if (!value) return undefined;
      const date = moment.isMoment(value) ? value : moment(value);
      return moment(date.format('YYYY-MM-DD')).toDate();
    };
    const parseReading = (value) => {
      if (value === undefined || value === null || value === '') return null;
      const parsed = Number(String(value).replace(/,/g, ''));
      return Number.isFinite(parsed) ? parsed : null;
    };
    const validationState = useMemo(() => {
      const parsedStartReading = parseReading(watchedStartReading);
      const parsedEndReading = parseReading(watchedEndReading);

      return {
        parsedStartReading,
        parsedEndReading,
        datesAreOutOfOrder:
          watchedStart && watchedEnd && moment(watchedStart).isAfter(watchedEnd, 'day'),
        readingsAreOutOfOrder:
          parsedStartReading !== null &&
          parsedEndReading !== null &&
          parsedStartReading > parsedEndReading,
      };
    }, [watchedEnd, watchedEndReading, watchedStart, watchedStartReading]);
    const { parsedStartReading, parsedEndReading, datesAreOutOfOrder, readingsAreOutOfOrder } =
      validationState;
    const startReadingTooltip = intl.formatMessage({
      id: 'startReadingMustBeLessThanEndReading',
      defaultMessage: 'Start reading must be less than or equal to end reading.',
    });
    const endReadingTooltip = intl.formatMessage({
      id: 'endReadingMustBeGreaterThanStartReading',
      defaultMessage: 'End reading must be greater than or equal to start reading.',
    });

    const utilityTypeOptions = Object.keys(activeUtilities)
      .filter((u) => activeUtilities[u])
      .map((type) => ({ value: UtilityType[type], text: intl.formatMessage({ id: type }) }));

    let currentUtilityType;
    let utilityUnitOptions = [];
    if (isNumber(watchedType)) {
      currentUtilityType = enumToString(UtilityType, watchedType);
      const currentUtilityUnits = utilityUnits[currentUtilityType];
      utilityUnitOptions = Object.keys(currentUtilityUnits || {}).map((unit) => ({
        value: currentUtilityUnits[unit],
        text: unit,
      }));
    }

    const lineItemOptionsByType = {
      Water: [
        { value: 'base', text: 'Base' },
        { value: 'waterUsage', text: 'Water Usage' },
        { value: 'tieredUsageCharge', text: 'Tiered Usage Charge' },
        { value: 'Meter Charge', text: 'Meter Charge' },
        { value: 'taxes', text: 'Taxes & Fees' },
        { value: 'other', text: 'Other' },
      ],
      Electric: [
        { value: 'base', text: 'Base' },
        { value: 'electricityUsage', text: 'Electricity Usage(kWh)' },
        { value: 'generationCharge', text: 'Generation Charge' },
        { value: 'transmissionCharge', text: 'Transmission Charge' },
        { value: 'distributionCharge', text: 'Distribution Charge' },
        { value: 'fuel', text: 'Fuel/Power Cost Adjustment' },
        { value: 'renewableFee', text: 'Renewable/Green Energy Fee' },
        { value: 'taxes', text: 'Taxes & Fees' },
        { value: 'other', text: 'Other' },
      ],
      Gas: [
        { value: 'base', text: 'Base' },
        { value: 'gasUsage', text: 'Gas Usage(Therms)' },
        { value: 'gasSupplyCommodityCharge', text: 'Gas Supply/Commodity Charge' },
        { value: 'deliveryDistributionCharge', text: 'Delivery/Distribution Charge' },
        { value: 'weatherAdjustment', text: 'Weather Adjustment' },
        { value: 'taxes', text: 'Taxes & Fees' },
        { value: 'other', text: 'Other' },
      ],
      Sewer: [
        { value: 'base', text: 'Base' },
        { value: 'serviceCharge', text: 'Sewer Service Charge' },
        { value: 'sewerUsageCharge', text: 'Sewer Usage Charge' },
        { value: 'wasteWaterTreatmentFee', text: 'Waste Water Treatment Fee' },
        { value: 'environmentalFee', text: 'Environmental Compliance Fee' },
        { value: 'taxes', text: 'Taxes & Fees' },
        { value: 'other', text: 'Other' },
      ],
    };
    const lineItemOptions = (currentUtilityType && lineItemOptionsByType[currentUtilityType]) || [];
    const parsedCost = parseFloat(String(watchedCost || '').replace(/,/g, ''));
    const lineItemsTotal = (watchedLineItems || []).reduce((sum, li) => {
      const amt = parseFloat(String(li?.amount || '').replace(/,/g, '')) || 0;
      return sum + amt;
    }, 0);
    const amountsMismatch =
      Number.isFinite(parsedCost) && Number.isFinite(lineItemsTotal)
        ? Number(parsedCost.toFixed(2)) !== Number(lineItemsTotal.toFixed(2))
        : false;

    useEffect(() => {
      const src = Array.isArray(initialValues?.lineItems)
        ? initialValues.lineItems
        : Array.isArray(initialValues?.items)
        ? initialValues.items
        : Array.isArray(initialValues?.line_items)
        ? initialValues.line_items
        : [];

      const newMappedLineItems = src.map((li) => ({
        name: li?.name ?? '',
        description: li?.description ?? '',
        bop: li?.startReading ?? '',
        eop: li?.endReading ?? '',
        amount: li?.cost ?? '',
        type:
          typeof li?.type === 'number'
            ? Number(li.type)
            : typeof li?.type === 'string' && UtilityType[li.type] !== undefined
            ? UtilityType[li.type]
            : undefined,
      }));

      reset({
        type: initialValues?.type ?? UtilityType[currentUtility],
        multiBills: initialValues?.multiBills ?? [],
        ...(initialValues || {}),
        lineItems: newMappedLineItems,
      });

      replaceLineItems(newMappedLineItems);

      if (!isControlledMultiBill) {
        setInternalMultiBill((newMappedLineItems || []).length > 0);
      }
    }, [initialValues?.id]);

    useEffect(() => {
      if (setSubmitDisabled) {
        setSubmitDisabled(Boolean(datesAreOutOfOrder || readingsAreOutOfOrder));
      }
    }, [datesAreOutOfOrder, readingsAreOutOfOrder, setSubmitDisabled]);

    useEffect(() => {
      if (datesAreOutOfOrder) {
        setValue('end', watchedStart, { shouldDirty: true });
      }
    }, [datesAreOutOfOrder, setValue, watchedStart]);

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit((vals) => onSubmit && onSubmit(vals))(),
    }));

    const typeChangedHandler = () => {
      setValue('unit', '');
      setValue('lineItems', []);
    };
    const handleMultiBillToggle = (checked) => {
      if (!isControlledMultiBill) {
        setInternalMultiBill(checked);
      }
      if (onMultiBillChange) {
        onMultiBillChange(checked);
      }
      if (!checked) {
        setValue('lineItems', []);
      }
    };
    const handleStartDateChange = useCallback(
      (value, onChange) => {
        onChange(value);
        if (value && watchedEnd && moment(value).isAfter(watchedEnd, 'day')) {
          setValue('end', value, { shouldDirty: true });
        }
      },
      [setValue, watchedEnd]
    );
    const handleEndDateChange = useCallback(
      (value, onChange) => {
        if (value && watchedStart && moment(value).isBefore(watchedStart, 'day')) {
          onChange(watchedStart);
          return;
        }
        onChange(value);
      },
      [watchedStart]
    );
    return (
      <FormProvider {...methods}>
        <div className="form-row full no-padding">
          <div>
            <input
              type="checkbox"
              id="multiBill"
              label={intl.formatMessage({ id: 'multiBill', defaultMessage: 'Multi-Bill' })}
              style={{ marginRight: '8px' }}
              checked={isMultiBill}
              onChange={(e) => handleMultiBillToggle(e.target.checked)}
            />
            <label htmlFor="multiBill">
              {intl.formatMessage({ id: 'multiBill', defaultMessage: 'Multi-Bill' })}
            </label>
          </div>
          <div className="form-group">
            <label htmlFor="type">{intl.formatMessage({ id: 'type' })}</label>
            <Controller
              name="type"
              render={({ field: { value, onChange } }) =>
                requiredSelect({
                  input: {
                    value,
                    onChange: (e) => {
                      typeChangedHandler();
                      onChange(Number(e?.target?.value ?? e));
                    },
                  },
                  id: 'type',
                  className: 'form-control',
                  options: utilityTypeOptions,
                })
              }
            />
          </div>
        </div>
        <div className="form-row full no-padding">
          <div className="form-group">
            <label htmlFor="name">{intl.formatMessage({ id: 'description' })}</label>
            <Controller
              name="name"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'name',
                  type: 'text',
                  className: 'form-control',
                  placeholder: intl.formatMessage({ id: 'utilityNamePlaceholder' }),
                })
              }
            />
          </div>
        </div>
        <div className="form-row half no-padding">
          <div className="form-group">
            <label htmlFor="startDate" className="mr-2">
              {intl.formatMessage({ id: 'startDate' })}
            </label>
            <Controller
              name="start"
              render={({ field }) =>
                requiredDatePicker({
                  input: {
                    ...field,
                    onChange: (value) => handleStartDateChange(value, field.onChange),
                  },
                  id: 'start',
                  type: 'date',
                  className: 'form-control',
                  placeholderText: intl.formatMessage({ id: 'utilityStartDatePlaceholder' }),
                  maxDate: getDatePickerDate(watchedEnd),
                })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="endDate" className="mr-2">
              {intl.formatMessage({ id: 'endDate' })}
            </label>
            <Controller
              name="end"
              render={({ field }) =>
                requiredDatePicker({
                  input: {
                    ...field,
                    onChange: (value) => handleEndDateChange(value, field.onChange),
                  },
                  id: 'end',
                  type: 'date',
                  className: 'form-control',
                  placeholderText: intl.formatMessage({ id: 'utilityEndDatePlaceholder' }),
                  minDate: getDatePickerDate(watchedStart),
                })
              }
            />
          </div>
        </div>
        <div className="form-row half no-padding">
          <div className="form-group">
            <label htmlFor="startReading">{intl.formatMessage({ id: 'startReading' })}</label>
            <Controller
              name="startReading"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'startReading',
                  type: 'text',
                  decimal: '4',
                  commas: 'true',
                  className: `form-control ${readingsAreOutOfOrder ? 'is-invalid' : ''}`,
                  placeholder: intl.formatMessage({ id: 'utilityReadingPlaceholder' }),
                  title: parsedEndReading !== null ? startReadingTooltip : undefined,
                })
              }
            />
            {readingsAreOutOfOrder && parsedEndReading !== null && (
              <small className="text-danger d-block mt-1">{startReadingTooltip}</small>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="endReading">{intl.formatMessage({ id: 'endReading' })}</label>
            <Controller
              name="endReading"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'endReading',
                  type: 'text',
                  decimal: '4',
                  commas: 'true',
                  className: `form-control ${readingsAreOutOfOrder ? 'is-invalid' : ''}`,
                  placeholder: intl.formatMessage({ id: 'utilityReadingPlaceholder' }),
                  title: parsedStartReading !== null ? endReadingTooltip : undefined,
                })
              }
            />
            {readingsAreOutOfOrder && parsedStartReading !== null && (
              <small className="text-danger d-block mt-1">{endReadingTooltip}</small>
            )}
          </div>
        </div>
        <div className="form-row full no-padding">
          <div className="form-group">
            <label htmlFor="unit">{intl.formatMessage({ id: 'readingUnit' })}</label>
            <Controller
              name="unit"
              render={({ field: { value, onChange } }) =>
                requiredSelect({
                  input: {
                    value,
                    onChange: (e) => {
                      const nextValue = e?.target?.value ?? e;
                      onChange(nextValue === '' ? '' : Number(nextValue));
                    },
                  },
                  id: 'unit',
                  className: 'form-control',
                  options: [
                    {
                      value: '',
                      text: intl.formatMessage({
                        id: 'pleaseSelect',
                        defaultMessage: 'Please select',
                      }),
                      disabled: true,
                    },
                    ...utilityUnitOptions,
                  ],
                })
              }
            />
          </div>
        </div>
        <div className="form-row half no-padding">
          <div className="form-group">
            <label htmlFor="cost">{intl.formatMessage({ id: 'amount' })}</label>
            <Controller
              name="cost"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'cost',
                  type: 'text',
                  decimal: '2',
                  commas: 'true',
                  className: 'form-control',
                  placeholder: intl.formatMessage({ id: 'utilityAmountPlaceholder' }),
                })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="currency">{intl.formatMessage({ id: 'currency' })}</label>
            <Controller
              name="currency"
              render={({ field: { value, onChange } }) =>
                requiredSelect({
                  input: { value, onChange },
                  id: 'currency',
                  className: 'form-control',
                  options: [
                    { value: 'USD', text: 'USD' },
                    { value: 'EUR', text: 'EUR' },
                  ],
                })
              }
            />
          </div>
        </div>
        <div className="form-row full no-padding">
          <div className="form-group">
            <label htmlFor="resourceLink">{intl.formatMessage({ id: 'resourceLink' })}</label>
            <Controller
              name="resourceLink"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'resourceLink',
                  type: 'text',
                  className: 'form-control',
                  placeholder: intl.formatMessage({ id: 'resourceLinkPlaceholder' }),
                })
              }
            />
          </div>
        </div>
        {isMultiBill && (
          <div className="form-row full no-padding">
            <div className="form-group" style={{ padding: 10 }}>
              {lineItemFields.map((item, index) => {
                const li = watchedLineItems[index] || {};
                const bopVal = li?.bop;
                const eopVal = li?.eop;
                const usageExists =
                  bopVal !== undefined && bopVal !== '' && eopVal !== undefined && eopVal !== '';
                const headerStart = watchedStart
                  ? moment.isMoment(watchedStart)
                    ? watchedStart.format('MMM D, YYYY')
                    : moment(watchedStart).format('MMM D, YYYY')
                  : '-';
                const headerEnd = watchedEnd
                  ? moment.isMoment(watchedEnd)
                    ? watchedEnd.format('MMM D, YYYY')
                    : moment(watchedEnd).format('MMM D, YYYY')
                  : '-';
                const unitText = (utilityUnitOptions.find((o) => o.value === watchedUnit) || {})
                  .text;
                return (
                  <div
                    key={item.id}
                    className="form-row full no-padding"
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 20,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ padding: '10px 10px 0 10px', backgroundColor: '#F5F5F5' }}>
                      <div
                        className="form-group"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {intl.formatMessage(
                            { id: 'lineItemNumber', defaultMessage: 'Line Item #{num}' },
                            { num: index + 1 }
                          )}
                        </div>
                        <i
                          className="uil uil-trash-alt"
                          onClick={() => removeLineItem(index)}
                          style={{ marginLeft: 8, cursor: 'pointer' }}
                        ></i>
                      </div>
                    </div>
                    {usageExists && (
                      <div className="form-group" style={{ color: '#6b7280', marginBottom: 8 }}>
                        {intl.formatMessage(
                          {
                            id: 'usageRange',
                            defaultMessage: 'Usage: {bop} → {eop}{unit}',
                          },
                          { bop: bopVal, eop: eopVal, unit: unitText ? ` ${unitText}` : '' }
                        )}
                      </div>
                    )}
                    <div style={{ padding: '0 10px' }}>
                      <div className="form-row full no-padding">
                        <div className="form-group">
                          <label htmlFor={`lineItemName_${index}`}>
                            {intl.formatMessage({ id: 'name' })}
                          </label>
                          <Controller
                            name={`lineItems.${index}.name`}
                            render={({ field }) =>
                              requiredInput({
                                input: field,
                                id: `lineItemName_${index}`,
                                type: 'text',
                                className: 'form-control',
                                placeholder: intl.formatMessage({ id: 'name' }),
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="form-row half no-padding">
                        <div className="form-group">
                          <label htmlFor={`lineItemType_${index}`}>
                            {intl.formatMessage({ id: 'type' })}
                          </label>
                          <Controller
                            name={`lineItems.${index}.type`}
                            render={({ field: { value, onChange } }) =>
                              requiredSelect({
                                input: {
                                  value: isNumber(value) ? value : watchedType,
                                  onChange: (e) => onChange(Number(e?.target?.value ?? e)),
                                },
                                id: `lineItemType_${index}`,
                                className: 'form-control',
                                options: utilityTypeOptions,
                              })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`lineItemAmount_${index}`}>
                            {intl.formatMessage({ id: 'amount' })}
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <Controller
                                name={`lineItems.${index}.amount`}
                                render={({ field }) =>
                                  requiredInput({
                                    input: field,
                                    id: `lineItemAmount_${index}`,
                                    type: 'text',
                                    decimal: '2',
                                    commas: 'true',
                                    className: 'form-control',
                                    placeholder: intl.formatMessage({
                                      id: 'utilityAmountPlaceholder',
                                    }),
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor={`lineItemDescription_${index}`}>
                          {intl.formatMessage({ id: 'description' })}
                        </label>
                        <Controller
                          name={`lineItems.${index}.description`}
                          render={({ field }) =>
                            requiredInput({
                              input: field,
                              id: `lineItemDescription_${index}`,
                              type: 'text',
                              className: 'form-control',
                              placeholder: intl.formatMessage({ id: 'description' }),
                            })
                          }
                        />
                      </div>
                      <div className="form-row half no-padding">
                        <div className="form-group">
                          <label htmlFor={`lineItemBop_${index}`}>
                            {intl.formatMessage({ id: 'startReading' })}
                          </label>
                          <Controller
                            name={`lineItems.${index}.bop`}
                            render={({ field }) =>
                              requiredInput({
                                input: field,
                                id: `lineItemBop_${index}`,
                                type: 'text',
                                decimal: '4',
                                commas: 'true',
                                className: 'form-control',
                                placeholder: intl.formatMessage({
                                  id: 'utilityReadingPlaceholder',
                                }),
                              })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`lineItemEop_${index}`}>
                            {intl.formatMessage({ id: 'endReading' })}
                          </label>
                          <Controller
                            name={`lineItems.${index}.eop`}
                            render={({ field }) =>
                              requiredInput({
                                input: field,
                                id: `lineItemEop_${index}`,
                                type: 'text',
                                decimal: '4',
                                commas: 'true',
                                className: 'form-control',
                                placeholder: intl.formatMessage({
                                  id: 'utilityReadingPlaceholder',
                                }),
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  appendLineItem({
                    name: '',
                    description: '',
                    bop: '',
                    eop: '',
                    amount: '',
                    type: isNumber(watchedType) ? watchedType : 0,
                  })
                }
                disabled={lineItemOptions.length === 0}
              >
                {intl.formatMessage({ id: 'addLineItem', defaultMessage: '+ Add Line Item' })}
              </button>
              <div style={{ marginTop: '8px' }}>
                {intl.formatMessage(
                  { id: 'lineItemsTotal', defaultMessage: 'Line items total: ${total}' },
                  { total: lineItemsTotal.toFixed(2) }
                )}
              </div>
              {amountsMismatch && (
                <div style={{ marginTop: '4px' }}>
                  {intl.formatMessage({
                    id: 'totalsDontMatchOk',
                    defaultMessage: "Totals don't match (this is okay)",
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </FormProvider>
    );
  }
);

UtilityEditForm.propTypes = {
  utilityUnits: PropTypes.shape({}).isRequired,
  activeUtilities: PropTypes.shape({}).isRequired,
  currentUtility: PropTypes.string.isRequired,
  intl: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func,
  initialValues: PropTypes.shape({}),
  setSubmitDisabled: PropTypes.func,
};

const mapStateToProps = (state) => ({
  activeUtilities: selectActiveUtilities(state),
});

const ConnectedUtilityForm = connect(mapStateToProps, null, null, { forwardRef: true })(
  UtilityEditForm
);

export default injectIntl(ConnectedUtilityForm, { forwardRef: true });
